/**
 * How long the browser waits for one `/ai/knowledge/*` answer. Sized for the
 * 60s cap the route had as a Vercel function; the backend that serves it now
 * has none of its own, so this bound is what stops a stalled connection.
 */
import { bobcatApiUrl } from '../../utils/bobcat-api';
export const POST_TIMEOUT_MS = 75_000;

/**
 * `POST /ai/knowledge/<route>` → its JSON body, or throws its `detail`. Gives
 * up after `POST_TIMEOUT_MS`: without a bound, a request that never answers
 * left the setup awaiting for good behind a progress toast that never closes
 * (PSTUDIO-4458).
 */
export const postKnowledge = async <T>(
  route: string,
  body: unknown
): Promise<T> => {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, POST_TIMEOUT_MS);
  try {
    const res = await fetch(bobcatApiUrl(`/ai/knowledge/${route}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const json = (await res.json().catch(() => null)) as
      | (T & { detail?: string; error?: string })
      | null;
    if (!res.ok || !json) {
      throw new Error(json?.detail ?? json?.error ?? res.statusText);
    }
    return json;
  } catch (err) {
    if (timedOut) {
      throw new Error(
        `The server did not answer in ${POST_TIMEOUT_MS / 1000}s.`
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
};
